import { resetLocalAuthKey, setLocalAuthKey } from '@/common/auth';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { SignupForm, SignupFormValues } from './form';
import { useCustomCreateUserMutation } from './graphql.generated';
import { SignupHeader } from './header/component';

export const SignupPage = () => {
	const navigate = useNavigate();
	const { isDesktop } = useLayoutContext();
	const [customCreateUser, { error, loading: creatingUser }] = useCustomCreateUserMutation();
	const ref = useRef<FormikProps<SignupFormValues>>(null);

	const handleSubmit = useCallback(
		async (values: SignupFormValues) => {
			try {
				await resetLocalAuthKey();
				// TODO: handle the scenario where the user already exists (via email address)
				const { data } = await customCreateUser({
					variables: {
						input: {
							email: values.email.toLowerCase(),
							firstName: values.firstName,
							lastName: values.lastName,
							password: values.password,
							confirm: values.reenterPassword,
						},
					},
				});

				if (data?.customCreateUser?.authToken) {
					await setLocalAuthKey(data.customCreateUser.authToken);
					navigate({ to: '/signup-details' });
				}
			} catch (error) {
				if ((error as Error).message === 'User already exists') {
					navigate({
						to: '/request-password-reset',
						search: { email: values.email.toLowerCase() },
					});
				} else {
					console.error(error);
				}
			}
		},
		[customCreateUser, navigate]
	);

	return (
		<PrimaryLayout
			footer={{
				children: [
					error ? (
						<StandardButton onClick={() => navigate({ to: '/home' })}>Home</StandardButton>
					) : (
						<>
							<StandardButton variant="light" onClick={() => navigate({ to: '/welcome' })}>
								Back
							</StandardButton>
							<StandardButton loading={creatingUser} onClick={() => ref.current?.submitForm()}>
								Sign Up
							</StandardButton>
						</>
					),
				],
			}}
		>
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Flex direction="column" align="center" justify="flex-start" w="100%">
					<SignupHeader />
					<ContentPanel pb={250} noPad>
						<CustomContentSection
							location={ContentSectionLocation.SignUp}
							placement={ContentSectionPlacement.Top}
						/>
						{error ? (
							<Title order={2} ta="center" mt="xl">
								An error occurred, please try again later or contact support.
							</Title>
						) : (
							<SignupForm onSubmit={handleSubmit} formRef={ref} />
						)}
						<CustomContentSection
							location={ContentSectionLocation.SignUp}
							placement={ContentSectionPlacement.Middle}
						/>
						<CustomContentSection
							location={ContentSectionLocation.SignUp}
							placement={ContentSectionPlacement.Bottom}
						/>
						{isDesktop && (
							<Flex w="100%" align="center" pt="xl" justify="flex-start">
								<Flex direction="row" w={300} gap="lg">
									<StandardButton loading={creatingUser} onClick={() => ref.current?.submitForm()}>
										Sign Up
									</StandardButton>
									<StandardButton variant="light" onClick={() => navigate({ to: '/welcome' })}>
										Back
									</StandardButton>
								</Flex>
							</Flex>
						)}
					</ContentPanel>
				</Flex>
			</Flex>
		</PrimaryLayout>
	);
};
